import { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiSearch,
  FiBell as FiDumbbell,
  FiChevronLeft,
  FiChevronRight,
  FiPlay,
  FiImage,
} from "react-icons/fi";
import { Exercise } from "../../types";
import { useAuth } from "../../context/AuthContext";
import { getVideoThumbnailUrl } from "../../services/videos";
import exercicies from "../../services/exercicies";
import ExercicieModal from "../../components/exercises/ExercicieModal";
import { ExerciseImage } from "../../components/exercises/ExerciseImage";
import Skeleton from "../../components/ui/Skeleton";

function Exercises() {
  const { getUser } = useAuth();
  const professionalId = getUser()?.id;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exerciseModal, setExerciseModal] = useState<Exercise | null>(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<"all" | "mine">("all");
  const pageSize = 15;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const loadExercises = useCallback(async () => {
    if (!professionalId) return;

    setLoading(true);
    try {
      const data = await exercicies.list_all(
        String(professionalId),
        page,
        pageSize,
        debouncedSearch,
        filter === "mine"
      );
      setExercises(data);
    } catch (error) {
      console.error("Erro ao carregar exercícios:", error);
    } finally {
      setLoading(false);
    }
  }, [professionalId, page, pageSize, debouncedSearch, filter]);

  useEffect(() => {
    loadExercises();
  }, [loadExercises]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (newFilter: "all" | "mine") => {
    setFilter(newFilter);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 py-4 sm:py-6 md:px-6 2xl:px-10">
      {/* Header Premium */}
      <div className="mb-4 sm:mb-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-purple-600 to-purple-700 p-4 sm:p-6 shadow-lg">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>

          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="rounded-xl bg-white/20 p-2.5 sm:p-3 backdrop-blur-sm">
                <FiDumbbell className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  Exercícios
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-purple-100">
                  Crie e gerencie sua biblioteca de exercícios personalizados
                </p>
                <p className="mt-0.5 text-[10px] sm:text-xs text-purple-200 font-medium">
                  ✨ Adicione quantos exercícios precisar para seus treinos
                </p>
              </div>
            </div>

            <button
              onClick={() =>
                setExerciseModal({
                  name: "",
                  image: "",
                  video: "",
                } as Exercise)
              }
              className="relative inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-purple-600 shadow-lg transition-all hover:scale-105 hover:shadow-xl active:scale-95 touch-manipulation animate-pulse hover:animate-none"
            >
              <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-[10px] font-bold text-white shadow-lg">
                +
              </div>
              <FiPlus size={20} />
              <span className="hidden sm:inline">Adicionar Exercício</span>
              <span className="sm:hidden">Adicionar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Card Principal */}
      <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden">
        {/* Header do Card com Search e Filtro */}
        <div className="border-b border-stroke bg-gray-50 px-3 sm:px-6 py-4 sm:py-5 dark:border-strokedark dark:bg-gray-900/20">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <div className="h-1 w-6 sm:w-8 rounded bg-purple-600"></div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                  Lista de Exercícios
                </h3>
              </div>

              <div className="relative w-full sm:w-auto sm:max-w-xs">
                <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
                  <FiSearch className="text-gray-400" size={18} />
                </span>
                <input
                  type="text"
                  placeholder="Buscar exercício..."
                  value={search}
                  onChange={({ target }) => handleSearchChange(target.value)}
                  className="w-full rounded-lg sm:rounded-xl border border-stroke bg-white py-2 sm:py-2.5 pl-10 sm:pl-12 pr-3 sm:pr-4 text-sm sm:text-base outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation"
                />
              </div>
            </div>

            {/* Filtros Premium */}
            <div className="inline-flex rounded-lg sm:rounded-xl border border-stroke bg-gray-100 p-1 text-xs sm:text-sm font-semibold dark:border-strokedark dark:bg-gray-900/40">
              <button
                onClick={() => handleFilterChange("all")}
                className={`flex-1 rounded-md sm:rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 transition-all touch-manipulation ${
                  filter === "all"
                    ? "bg-white text-purple-600 shadow-sm dark:bg-boxdark dark:text-purple-400"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => handleFilterChange("mine")}
                className={`flex-1 rounded-md sm:rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 transition-all touch-manipulation ${
                  filter === "mine"
                    ? "bg-white text-purple-600 shadow-sm dark:bg-boxdark dark:text-purple-400"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                }`}
              >
                Somente Meus
              </button>
            </div>
          </div>
        </div>

        {/* Banner Informativo */}
        {exercises.length > 0 && !loading && (
          <div className="border-b border-stroke bg-gradient-to-r from-purple-50 to-blue-50 px-3 sm:px-6 py-3 dark:from-purple-900/10 dark:to-blue-900/10 dark:border-strokedark">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="flex-shrink-0 rounded-lg bg-purple-100 p-1.5 sm:p-2 dark:bg-purple-900/30">
                <FiPlus
                  className="text-purple-600 dark:text-purple-400"
                  size={16}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                  Adicione mais exercícios sempre que precisar
                </p>
                <p className="mt-0.5 text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                  Clique no botão "Adicionar Exercício" no topo para criar novos
                  exercícios personalizados
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Conteúdo */}
        <div className="p-3 sm:p-6">
          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-lg sm:rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark"
                >
                  <Skeleton className="h-32 sm:h-48 w-full" />
                  <div className="p-2 sm:p-4">
                    <Skeleton className="mb-2 h-4 sm:h-5 w-3/4" />
                    <Skeleton className="h-3 sm:h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : exercises.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {exercises.map((exercise: Exercise) => (
                <div
                  key={exercise.id}
                  onClick={() => setExerciseModal(exercise)}
                  role="button"
                  tabIndex={0}
                  className="group cursor-pointer overflow-hidden rounded-lg sm:rounded-xl border border-stroke bg-white shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 dark:border-strokedark dark:bg-boxdark touch-manipulation"
                >
                  {/* Imagem ou thumbnail do vídeo */}
                  <div className="relative h-32 sm:h-48 w-full overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                    {exercise.image_url || exercise.image ? (
                      <>
                        <ExerciseImage
                          exercise={exercise}
                          width={240}
                          height={240}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          alt={exercise.name}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </>
                    ) : exercise.video?.includes(".m3u8") &&
                      getVideoThumbnailUrl(exercise.video) ? (
                      <>
                        <img
                          src={getVideoThumbnailUrl(exercise.video)!}
                          alt={exercise.name}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <div className="rounded-xl bg-gray-200 p-3 sm:p-4 dark:bg-gray-700">
                          <FiImage
                            size={24}
                            className="text-gray-400 sm:w-8 sm:h-8"
                          />
                        </div>
                      </div>
                    )}

                    {/* Badge de vídeo */}
                    {exercise.video && (
                      <div className="absolute right-2 top-2 sm:right-3 sm:top-3 rounded-full bg-purple-600 p-1.5 sm:p-2 shadow-lg backdrop-blur-sm group-hover:scale-110 transition-transform">
                        <FiPlay className="text-white" size={12} />
                      </div>
                    )}

                    {/* Overlay de hover */}
                    <div className="absolute inset-0 flex items-center justify-center bg-purple-600/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="text-center text-white">
                        <p className="text-xs sm:text-sm font-semibold">
                          Clique para editar
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Informações */}
                  <div className="p-2 sm:p-4">
                    <h4 className="text-xs sm:text-base font-semibold text-gray-900 dark:text-white line-clamp-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                      {exercise.name}
                    </h4>
                    {exercise.description && (
                      <p className="mt-1 sm:mt-2 line-clamp-2 text-[10px] sm:text-sm text-gray-600 dark:text-gray-400">
                        {exercise.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16">
              <div className="mb-4 sm:mb-6 rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 p-4 sm:p-6 dark:from-purple-900/20 dark:to-purple-800/20">
                <FiDumbbell
                  size={40}
                  className="text-purple-600 dark:text-purple-400 sm:w-12 sm:h-12"
                />
              </div>
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {search
                  ? "Nenhum exercício encontrado"
                  : "Comece criando seus exercícios!"}
              </p>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center max-w-md px-4">
                {search
                  ? "Tente buscar por outro nome ou ajuste os filtros"
                  : "Você pode adicionar quantos exercícios quiser para montar treinos personalizados"}
              </p>
              {!search && (
                <div className="mt-3 rounded-lg bg-purple-50 px-4 py-2 dark:bg-purple-900/20">
                  <p className="text-xs sm:text-sm text-purple-700 dark:text-purple-300 font-medium">
                    💡 Dica: Crie exercícios com fotos e vídeos para facilitar o
                    entendimento dos alunos
                  </p>
                </div>
              )}
              {!search && (
                <button
                  onClick={() =>
                    setExerciseModal({
                      id: 0,
                      name: "Novo exercício",
                      image: "",
                      video: "",
                    } as Exercise)
                  }
                  className="mt-4 sm:mt-6 inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-bold text-white shadow-lg transition-all hover:bg-purple-700 hover:scale-105 active:scale-95 touch-manipulation animate-pulse hover:animate-none"
                >
                  <FiPlus size={20} />
                  Adicionar Meu Primeiro Exercício
                </button>
              )}
            </div>
          )}
        </div>

        {/* Paginação Premium */}
        {exercises.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 border-t border-stroke bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 dark:border-strokedark dark:bg-gray-900/20">
            <div className="hidden sm:block text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Mostrando até {pageSize} exercícios por página
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setPage(page > 1 ? page - 1 : 1)}
                disabled={page === 1}
                className="inline-flex items-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-xl border-2 border-stroke px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-gray-700 transition-all hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed dark:border-strokedark dark:text-white dark:hover:bg-boxdark touch-manipulation active:scale-95 disabled:active:scale-100"
              >
                <FiChevronLeft size={16} />
                <span className="hidden sm:inline">Anterior</span>
              </button>

              <div className="flex items-center gap-2 rounded-lg sm:rounded-xl border-2 border-purple-200 bg-purple-50 px-3 sm:px-4 py-2 sm:py-2.5 dark:border-purple-800 dark:bg-purple-900/20">
                <span className="text-xs sm:text-sm font-bold text-purple-900 dark:text-purple-300">
                  Página {page}
                </span>
              </div>

              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={exercises.length < pageSize}
                className="inline-flex items-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-xl border-2 border-stroke px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-gray-700 transition-all hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed dark:border-strokedark dark:text-white dark:hover:bg-boxdark touch-manipulation active:scale-95 disabled:active:scale-100"
              >
                <span className="hidden sm:inline">Próximo</span>
                <FiChevronRight size={16} />
              </button>
            </div>

            <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400">
              Até {pageSize} por página
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <ExercicieModal
        isOpen={exerciseModal !== null}
        onBack={() => setExerciseModal(null)}
        data={exerciseModal}
        onSuccess={() => {
          setExerciseModal(null);
          loadExercises();
        }}
      />
    </div>
  );
}

export default Exercises;
